// src/utils/tabConfig.js
// Dynamic tab configuration system — each shop stores its own tabs array in Firestore.
// Tab IDs are stable identifiers; labels are display-only and fully editable by the owner.

export const TAB_TYPES = {
  HOME: 'home',        // HomePageTemplate sections editor/viewer
  CATALOG: 'catalog',  // Products & services grid
  COMMUNITY: 'community', // Community posts & events
};

// Core tab IDs that cannot be deleted (only hidden or renamed)
export const CORE_TAB_IDS = ['home', 'shop', 'profile'];

// Default tab set — matches the existing hardcoded home/shop/profile IDs
// so sessionStorage values and existing Firestore data stay compatible.
export const DEFAULT_TABS = [
  { id: 'home',    label: 'Home',      icon: 'Home',  type: 'home',      visible: true, order: 0 },
  { id: 'shop',    label: 'Shop',      icon: 'Store', type: 'catalog',   visible: true, order: 1 },
  { id: 'profile', label: 'Profile', icon: 'Users', type: 'community', visible: true, order: 2 },
];

// Business-type presets — applied when a new shop is created (can be overridden by owner)
const STORE_TAB_PRESETS = {
  restaurant: [
    { id: 'home',    label: 'Home',   icon: 'Home',         type: 'home',      visible: true, order: 0 },
    { id: 'shop',    label: 'Menu',   icon: 'BookOpen',     type: 'catalog',   visible: true, order: 1 },
    { id: 'profile', label: 'Events', icon: 'Calendar',     type: 'community', visible: true, order: 2 },
  ],
  food_truck: [
    { id: 'home',    label: 'Home',  icon: 'Home',     type: 'home',      visible: true, order: 0 },
    { id: 'shop',    label: 'Menu',  icon: 'Utensils', type: 'catalog',   visible: true, order: 1 },
    { id: 'profile', label: 'Posts', icon: 'Camera',   type: 'community', visible: true, order: 2 },
  ],
  services: [
    { id: 'home',    label: 'Home',     icon: 'Home',          type: 'home',      visible: true, order: 0 },
    { id: 'shop',    label: 'Services', icon: 'Briefcase',     type: 'catalog',   visible: true, order: 1 },
    { id: 'profile', label: 'Posts',    icon: 'MessageSquare', type: 'community', visible: true, order: 2 },
  ],
  freelance: [
    { id: 'home',    label: 'Home',      icon: 'Home',          type: 'home',      visible: true, order: 0 },
    { id: 'shop',    label: 'Portfolio', icon: 'Grid',          type: 'catalog',   visible: true, order: 1 },
    { id: 'profile', label: 'Updates',   icon: 'MessageSquare', type: 'community', visible: true, order: 2 },
  ],
  salon: [
    { id: 'home',    label: 'Home',     icon: 'Home',      type: 'home',      visible: true, order: 0 },
    { id: 'shop',    label: 'Services', icon: 'Scissors',  type: 'catalog',   visible: true, order: 1 },
    { id: 'profile', label: 'Gallery',  icon: 'Camera',    type: 'community', visible: true, order: 2 },
  ],
  fitness: [
    { id: 'home',    label: 'Home',    icon: 'Home',     type: 'home',      visible: true, order: 0 },
    { id: 'shop',    label: 'Classes', icon: 'Dumbbell', type: 'catalog',   visible: true, order: 1 },
    { id: 'profile', label: 'Feed',    icon: 'Users',    type: 'community', visible: true, order: 2 },
  ],
};

export const getDefaultTabs = (storeType) =>
  STORE_TAB_PRESETS[storeType] ?? DEFAULT_TABS;

// Normalize raw tabs from Firestore — fills missing fields and sorts by order.
// Falls back to store-type defaults when the shop has no tabs yet.
export const normalizeTabs = (rawTabs, storeType) => {
  if (!rawTabs?.length) return getDefaultTabs(storeType);
  return [...rawTabs]
    .filter(t => t?.id && t?.type)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

// Icon names available for tab icon selection (must match lucide-react exports)
export const TAB_ICON_OPTIONS = [
  'Home', 'Store', 'Users', 'Grid', 'Calendar', 'MessageSquare',
  'Package', 'Phone', 'MapPin', 'Star', 'BookOpen', 'Heart',
  'Info', 'Briefcase', 'Camera', 'Film', 'Music', 'Globe',
  'Mail', 'Clock', 'Award', 'Scissors', 'Coffee', 'Navigation',
  'ShoppingBag', 'Dumbbell', 'Palette', 'Truck', 'Image', 'Utensils',
];
